/**
 * Formats a decimal string for display without going through a float.
 *
 * @param {string} value - Decimal text from the API.
 * @param {number} places - Places to show.
 * @returns {string} The value trimmed or padded to the requested places.
 *
 * @remarks
 * Truncates rather than rounds, and never parses to a number. Credit balances
 * are carried as text precisely because they can exceed what a JS number holds
 * exactly, and Number() here would undo that on the last step.
 */
export const formatDecimal = (value, places = 2) => {
  const text = String(value ?? "0").trim();

  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    return "—";
  }

  const negative = text.startsWith("-");
  const [whole, fraction = ""] = (negative ? text.slice(1) : text).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (places === 0) {
    return `${negative ? "-" : ""}${grouped}`;
  }

  const padded = fraction.padEnd(places, "0").slice(0, places);

  return `${negative ? "-" : ""}${grouped}.${padded}`;
};

/**
 * Whether a decimal string represents zero.
 *
 * @param {string} value - Decimal text.
 * @returns {boolean} True when every digit is zero.
 */
export const isZero = (value) => /^-?0*(\.0*)?$/.test(String(value ?? "0").trim());

/**
 * Renders a timestamp as a short calendar date.
 *
 * @param {string|null} value - ISO timestamp, or null.
 * @returns {string} A readable date, or an em dash when never used.
 */
export const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Picks one feature's usage row out of the summary.
 *
 * @param {object|null} summary - Usage summary from the API.
 * @param {string} feature - AiBillableFeature key.
 * @returns {object|null} That feature's row, or null when absent.
 */
export const featureUsage = (summary, feature) =>
  (summary?.features ?? []).find((row) => row.feature === feature) ?? null;
